import { Component, useState, useEffect } from 'react'
import Select from 'react-select'
import { List, CellMeasurer, CellMeasurerCache } from 'react-virtualized'
import { colors } from '@global/constants'
import { isElementOutOfViewport } from '@utils/isElementOutOfViewport'
import { Theme } from 'Theme'
import { t } from '@lingui/core/macro'

const MAX_HEIGHT = 300
const MAX_OPTIONS_WITHOUT_SCROLL = 9
const MENU_OFFSET = 150
const MAX_CHARACTERS_TO_CREATE_NEW_LINE = 29

const cache = new CellMeasurerCache({
  defaultHeight: 33,
  fixedHeight: false,
})

const MenuList = ({ height: initialHeight, width, children, search }) => {
  const [height, setHeight] = useState(initialHeight)

  useEffect(() => {
    cache.clearAll()
  }, [search])

  useEffect(() => {
    setHeight(height)
  }, [cache, height])

  const noRowsComponent = () => <div style={{ padding: 4 }}>{t`No matching rows`}</div>

  return (
    <List
      height={height}
      overscanRowCount={10}
      noRowsRenderer={noRowsComponent}
      rowCount={(children || []).length || 0}
      rowHeight={cache.rowHeight}
      rowRenderer={({ index, key, style, parent }) => (
        <CellMeasurer cache={cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
          <div style={{ ...style, wordBreak: 'break-all', display: 'inline-flex' }}>
            {children[index]}
          </div>
        </CellMeasurer>
      )}
      width={width}
    />
  )
}

let tabPressed = false

export class ReactMultiSelect extends Component {
  constructor(props) {
    super(props)
    this.state = {
      selected: null,
      row: props.row,
      search: '',
    }
  }

  componentDidMount() {
    this.setState({
      ...this.props.data,
      search: '',
    })
  }

  handleChange(selectedItems) {
    this.props.updateData({
      ...this.state,
      selected: selectedItems,
    })
    this.props.close(tabPressed ? 'TAB' : null)
    tabPressed = false
  }

  handleKeyDown(e) {
    if (e.key === 'Tab') {
      tabPressed = true
    } else {
      tabPressed = false
    }
    if (e.key === 'Escape') {
      e.stopPropagation()
      e.preventDefault()
      if (this.props.selected[0]) {
        this.props.updateData({ ...this.state, selected: this.props.selected[0] })
      }
      this.props.close()
      return
    }
    if (e.key === 'Enter') {
      this.props.updateData({ ...this.state, selected: { value: e.target.value } })
      setTimeout(() => {
        this.props.close(true)
      }, 100)
    }
  }

  static getDerivedStateFromProps(props) {
    return {
      selected: props.selected,
      row: props.row,
    }
  }

  render() {
    const customStyles = {
      container: provided => ({
        ...provided,
        height: `${this.props.parent?.getClientRects()[0]?.height}px`,
        width: `${this.props.parent?.getClientRects()[0]?.width}px`,
      }),
      control: provided => ({
        ...provided,
        borderRadius: 0,
        borderColor: `${colors.blue.smokey} !important`,
        boxShadow: `0 0 0 1px ${colors.blue.smokey}`,
        height: '100%',
        width: '100%',
      }),
      clearIndicator: provided => ({
        ...provided,
        cursor: 'pointer',
        color: colors.red.main,
      }),
    }

    const CustomClearText = () => (
      <i className="material-icons material-icons-font md-theme-danger">{t`delete`}</i>
    )
    const ClearIndicator = props => {
      const {
        children = <CustomClearText />,
        getStyles,
        innerProps: { ref, ...restInnerProps },
      } = props
      return (
        <div {...restInnerProps} ref={ref} style={getStyles('clearIndicator', props)}>
          <div style={{ padding: '0px 5px' }}>{children}</div>
        </div>
      )
    }

    const calculateHeight = () => {
      const shouldCreateScroll = (this.props.options || []).length > MAX_OPTIONS_WITHOUT_SCROLL

      if (shouldCreateScroll) return MAX_HEIGHT

      const getChildHeight = (child, index) => {
        const isOptionGoingToCreateNewLine =
          (child.value || '').length >= MAX_CHARACTERS_TO_CREATE_NEW_LINE

        if (isOptionGoingToCreateNewLine) {
          return (
            ((child.value || '').length / MAX_CHARACTERS_TO_CREATE_NEW_LINE) * cache.defaultHeight
          )
        }

        return cache.getHeight(index)
      }

      const calculateDynamicHeight = () => {
        if (this.props.options.reduce) {
          return this.props.options.reduce((totalHeight, child, index) => {
            const childHeight = getChildHeight(child, index)
            return totalHeight + childHeight
          }, 0)
        }
        return 35
      }

      const calculatedHeight = Math.min(MAX_HEIGHT, calculateDynamicHeight())
      return calculatedHeight
    }

    const height = calculateHeight()
    const width = this.props.parent?.getClientRects()[0]?.width
    const hasMenuEnoughSpaceToOpenUp = isElementOutOfViewport({
      containerElement: this.props.parent,
      innerElementHeight: height,
      offset: MENU_OFFSET,
    })

    return (
      <Theme>
        <Select
          menuPlacement={hasMenuEnoughSpaceToOpenUp ? 'top' : 'auto'}
          menuPosition="fixed"
          closeMenuOnSelect={true}
          value={this.state.selected}
          defaultMenuIsOpen={true}
          autoFocus
          onKeyDown={this.handleKeyDown.bind(this)}
          tabSelectsValue={true}
          styles={customStyles}
          options={this.props.options}
          inputValue={this.state.search}
          onInputChange={val => {
            this.setState({ search: val })
          }}
          onChange={(val, e) => {
            this.handleChange(val, e)
          }}
          ref={this.props.ref}
          components={{
            DropdownIndicator: null,
            ClearIndicator,
            MenuList: props => (
              <MenuList width={width} height={height} {...props} search={this.state.search} />
            ),
          }}
        />
      </Theme>
    )
  }
}
